import type { ReactElement } from 'react';
import { Link, type To } from 'react-router';
import type { ConversationItem, Role } from '@chatapp-inbox/shared';
import { DETAILS_HEADING_TARGET, FOCUS_TARGET_ATTRIBUTE } from '../conversation/focus-targets';
import { AssignmentSection } from './AssignmentSection';
import { BitrixSection } from './BitrixSection';
import { ContactCard } from './ContactCard';
import { NotesSection } from './NotesSection';
import { StatusSection } from './StatusSection';
import { useNow } from './use-now';
import styles from './DetailsPanel.module.css';

export interface DetailsPanelProps {
  conversation: ConversationItem;
  /** Quem está usando: define o que pode fazer (administrador transfere; atendente assume/libera). */
  viewer: { id: string; role: Role };
  /** Rota do chat (usada em layout médio/estreito, onde o chat é outra tela). */
  chatHref: To;
}

/**
 * Painel de detalhes da conversa: contato, responsável, situação (aberta ou encerrada) e notas internas. Em tela larga fica ao lado
 * do chat; abaixo de 1100px é uma tela própria (`/c/:id/detalhes`) com o link de volta ao chat.
 * O painel rola por dentro (a página nunca rola).
 */
export function DetailsPanel({ conversation, viewer, chatHref }: DetailsPanelProps): ReactElement {
  const now = useNow();
  return (
    <section aria-label="Detalhes" className={styles.panel}>
      <header className={styles.header}>
        {/* Só em layout médio/estreito (CSS): no largo o chat já está ao lado. */}
        <Link to={chatHref} className={styles.back}>
          <span aria-hidden="true">←</span> Voltar para a conversa
        </Link>
        {/* Destino do foco quando a página alterna do chat para os detalhes (ver focus-targets.ts). */}
        <div className={styles.titleRow}>
          <h2
            tabIndex={-1}
            className={styles.title}
            {...{ [FOCUS_TARGET_ATTRIBUTE]: DETAILS_HEADING_TARGET }}
          >
            Detalhes
          </h2>
          {/* Só em tela larga (CSS): os detalhes abrem ao lado do chat e este "×" os recolhe. */}
          <Link to={chatHref} className={styles.close} aria-label="Fechar os detalhes">
            <span aria-hidden="true">×</span>
          </Link>
        </div>
      </header>
      {/*
        `key`: outra conversa remonta tudo (edição em andamento, confirmações, avisos, rolagem). Outra
        PESSOA também: se a conta muda em outra aba com esta tela aberta, o texto digitado pela pessoa
        anterior vive em estado local dos campos e não pode passar para a nova (o cache de rascunhos
        já foi descartado pela troca de sessão, mas o estado dos componentes montados não).
      */}
      <div key={`${conversation.id}:${viewer.id}`} className={styles.body}>
        <ContactCard conversation={conversation} now={now} />
        <AssignmentSection conversation={conversation} viewer={viewer} />
        <StatusSection conversation={conversation} />
        <BitrixSection conversation={conversation} />
        <NotesSection conversation={conversation} viewer={viewer} now={now} />
      </div>
    </section>
  );
}
